import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, IconButton, Tooltip, Collapse, MenuItem, TextField,
  CircularProgress,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  TrendingUp as MrrIcon,
  EventSeat as SeatsIcon,
  CreditCard as SubIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

const STATUS_CHIP: Record<string, { color: 'success' | 'error' | 'warning' | 'default' | 'info' }> = {
  Active: { color: 'success' },
  Trialing: { color: 'info' },
  PastDue: { color: 'error' },
  Cancelled: { color: 'default' },
};

function StatCard({ title, value, subtitle, icon, color }: {
  title: string; value: string; subtitle?: string; icon: React.ReactNode; color: string;
}) {
  return (
    <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.8rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

interface BillingOverview {
  mrrPence: number;
  mrrFormatted: string;
  arrPence: number;
  arrFormatted: string;
  totalTenants: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  pastDueSubscriptions: number;
  cancelledSubscriptions: number;
  totalActiveSeats: number;
  avgRevenuePerTenantPence: number;
  revenueByPlan: { plan: string; tenantCount: number; totalSeats: number; monthlyRevenuePence: number }[];
  subscriptionsByStatus: { active: number; trialing: number; pastDue: number; cancelled: number };
}

interface BillingTenant {
  id: string;
  tenantId: string;
  tenantName: string;
  plan: string;
  seatCount: number;
  monthlyAmountPence: number;
  status: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
  createdAt: string;
}

interface TenantBillingHistory {
  billing: any;
  events: { id: string; action: string; fromValue?: string; toValue?: string; performedByUserId?: string; timestamp: string }[];
  recentUsage: { year: number; month: number; planGenerations: number; transcriptsProcessed: number; aiTokensUsed: number }[];
}

export default function PlatformBillingPage() {
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [tenants, setTenants] = useState<BillingTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, TenantBillingHistory>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, tenantsData] = await Promise.all([
        platformApi.getBillingOverview(),
        platformApi.getBillingTenants({ status: statusFilter || undefined, plan: planFilter || undefined }),
      ]);
      setOverview(overviewData);
      setTenants(tenantsData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async (tenantId: string) => {
    setHistoryLoading(tenantId);
    try {
      const data = await platformApi.getTenantBillingHistory(tenantId);
      setHistory(prev => ({ ...prev, [tenantId]: data }));
    } catch {
      // Silently fail
    } finally {
      setHistoryLoading(null);
    }
  }, []);

  const toggleExpand = (tenantId: string) => {
    const isExpanding = expandedId !== tenantId;
    setExpandedId(prev => prev === tenantId ? null : tenantId);
    if (isExpanding && !history[tenantId]) {
      loadHistory(tenantId);
    }
  };

  const formatPence = (pence: number) => `\u00a3${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Get unique plans from tenants for filter
  const uniquePlans = Array.from(new Set(tenants.map(t => t.plan).filter(Boolean)));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PaymentIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Billing Centre</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Revenue, subscriptions, and tenant billing
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Past Due Alert */}
      {overview && overview.pastDueSubscriptions > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <strong>{overview.pastDueSubscriptions} subscription{overview.pastDueSubscriptions > 1 ? 's' : ''} past due</strong> — these tenants may have payment issues that need attention.
        </Alert>
      )}

      {overview && (
        <Stack spacing={2.5}>
          {/* Revenue Summary Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Monthly Recurring Revenue"
                value={overview.mrrFormatted}
                icon={<MrrIcon />}
                color={COLORS.success}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Annual Run Rate"
                value={overview.arrFormatted}
                subtitle="MRR x 12"
                icon={<MrrIcon />}
                color={COLORS.primary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active Subscriptions"
                value={String(overview.activeSubscriptions)}
                subtitle={`${overview.trialingSubscriptions} trialing`}
                icon={<SubIcon />}
                color={COLORS.secondary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Active Seats"
                value={String(overview.totalActiveSeats)}
                subtitle={overview.activeSubscriptions > 0 ? `Avg ${formatPence(overview.avgRevenuePerTenantPence)}/tenant` : undefined}
                icon={<SeatsIcon />}
                color={COLORS.warning}
              />
            </Grid>
          </Grid>

          {/* Revenue by Plan + Subscription Status */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
                    Revenue by Plan
                  </Typography>
                  {overview.revenueByPlan.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Plan</TableCell>
                            <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Tenants</TableCell>
                            <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Seats</TableCell>
                            <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Monthly Revenue</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, width: '30%' }}>Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overview.revenueByPlan.map(row => {
                            const totalRev = overview.revenueByPlan.reduce((s, r) => s + r.monthlyRevenuePence, 0);
                            const pct = totalRev > 0 ? (row.monthlyRevenuePence / totalRev) * 100 : 0;
                            return (
                              <TableRow key={row.plan}>
                                <TableCell>
                                  <Chip label={row.plan} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>{row.tenantCount}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>{row.totalSeats}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600 }}>
                                    {formatPence(row.monthlyRevenuePence)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{
                                        flex: 1, height: 6, borderRadius: 3,
                                        bgcolor: 'rgba(255,255,255,0.05)',
                                        '& .MuiLinearProgress-bar': { bgcolor: COLORS.success, borderRadius: 3 },
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, minWidth: 32, textAlign: 'right' }}>
                                      {pct.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No revenue data yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
                    Subscription Status
                  </Typography>
                  <Stack spacing={2}>
                    {[
                      { label: 'Active', count: overview.subscriptionsByStatus.active, color: COLORS.success },
                      { label: 'Trialing', count: overview.subscriptionsByStatus.trialing, color: COLORS.primary },
                      { label: 'Past Due', count: overview.subscriptionsByStatus.pastDue, color: COLORS.error },
                      { label: 'Cancelled', count: overview.subscriptionsByStatus.cancelled, color: COLORS.textSecondary },
                    ].map(item => {
                      const total = overview.activeSubscriptions + overview.trialingSubscriptions + overview.pastDueSubscriptions + overview.cancelledSubscriptions;
                      const pct = total > 0 ? (item.count / total) * 100 : 0;
                      return (
                        <Box key={item.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>{item.label}</Typography>
                            <Typography variant="body2" sx={{ color: item.color, fontWeight: 600 }}>
                              {item.count}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6, borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.05)',
                              '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 3 },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tenant Billing Table */}
          <Card sx={{ bgcolor: COLORS.bgPaper }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                  Tenant Billing
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Trialing">Trialing</MenuItem>
                    <MenuItem value="PastDue">Past Due</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Plan"
                    value={planFilter}
                    onChange={e => setPlanFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniquePlans.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, width: 40 }} />
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Tenant</TableCell>
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Plan</TableCell>
                      <TableCell align="center" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Seats</TableCell>
                      <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Monthly</TableCell>
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Period</TableCell>
                      <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Stripe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenants.map(t => (
                      <React.Fragment key={t.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& td': { borderColor: COLORS.gridLine } }}
                          onClick={() => toggleExpand(t.tenantId)}
                        >
                          <TableCell sx={{ width: 40, pr: 0 }}>
                            <IconButton size="small">
                              {expandedId === t.tenantId ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                            {t.tenantName}
                          </TableCell>
                          <TableCell>
                            <Chip label={t.plan} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>{t.seatCount}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600 }}>
                              {formatPence(t.monthlyAmountPence)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t.status}
                              size="small"
                              color={STATUS_CHIP[t.status]?.color || 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                              {t.currentPeriodStart
                                ? `${new Date(t.currentPeriodStart).toLocaleDateString()} - ${new Date(t.currentPeriodEnd!).toLocaleDateString()}`
                                : '---'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {t.stripeCustomerId ? (
                              <Tooltip title={`Customer: ${t.stripeCustomerId}${t.stripeSubscriptionId ? ` | Sub: ${t.stripeSubscriptionId}` : ''}`}>
                                <Chip label="Linked" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                              </Tooltip>
                            ) : (
                              <Chip label="No Stripe" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }} />
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderColor: COLORS.gridLine }}>
                            <Collapse in={expandedId === t.tenantId} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2 }}>
                                {historyLoading === t.tenantId ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : history[t.tenantId] ? (
                                  <Stack spacing={2}>
                                    {/* Stripe IDs */}
                                    <Stack direction="row" spacing={4} flexWrap="wrap">
                                      <Box>
                                        <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Tenant ID</Typography>
                                        <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                          {t.tenantId}
                                        </Typography>
                                      </Box>
                                      {t.stripeCustomerId && (
                                        <Box>
                                          <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Stripe Customer</Typography>
                                          <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {t.stripeCustomerId}
                                          </Typography>
                                        </Box>
                                      )}
                                      {t.stripeSubscriptionId && (
                                        <Box>
                                          <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Stripe Subscription</Typography>
                                          <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {t.stripeSubscriptionId}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Stack>

                                    {/* Subscription Events */}
                                    {history[t.tenantId].events.length > 0 && (
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary, mb: 1 }}>
                                          Subscription History
                                        </Typography>
                                        <TableContainer>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                                                <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>From</TableCell>
                                                <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>To</TableCell>
                                                <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {history[t.tenantId].events.map(e => (
                                                <TableRow key={e.id}>
                                                  <TableCell sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    <Chip label={e.action} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                                  </TableCell>
                                                  <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {e.fromValue || '---'}
                                                  </TableCell>
                                                  <TableCell sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {e.toValue || '---'}
                                                  </TableCell>
                                                  <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {new Date(e.timestamp).toLocaleString()}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    )}

                                    {/* Recent Usage */}
                                    {history[t.tenantId].recentUsage.length > 0 && (
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary, mb: 1 }}>
                                          Recent Usage
                                        </Typography>
                                        <TableContainer>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>Period</TableCell>
                                                <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>Plans</TableCell>
                                                <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>Transcripts</TableCell>
                                                <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, fontSize: '0.75rem' }}>AI Tokens</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {history[t.tenantId].recentUsage.map(u => (
                                                <TableRow key={`${u.year}-${u.month}`}>
                                                  <TableCell sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {new Date(u.year, u.month - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {u.planGenerations}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {u.transcriptsProcessed}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: COLORS.textPrimary, borderColor: COLORS.gridLine, fontSize: '0.8rem' }}>
                                                    {u.aiTokensUsed.toLocaleString()}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    )}

                                    {history[t.tenantId].events.length === 0 && history[t.tenantId].recentUsage.length === 0 && (
                                      <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 1 }}>
                                        No history or usage data available for this tenant.
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 1 }}>
                                    No billing data available.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {!loading && tenants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>No billing records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      )}

      {!loading && !overview && !error && (
        <Card sx={{ bgcolor: COLORS.bgPaper }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No billing data available</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
